import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService, getRemainingTime } from '@inyova/inyova-shared';
import { Account, Customer, eve2BannerLinks, FeatureFlags } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import * as ReportingActions from './reporting.actions';
import * as AppRootActions from '../../app.actions';
import * as fromReporting from './reporting.reducers';
import * as fromAppRoot from '../../app.reducers';

import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-reporting',
  templateUrl: './reporting.component.html',
  styleUrls: ['./reporting.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ReportingComponent implements OnDestroy, OnInit {
  protected readonly onDestroy$ = new Subject<void>();
  customer: Customer;
  currentAccount: Account;
  displayOddModal = false;
  displayGreennessSurvey = false;
  showGreennessNotification: null | boolean = null;
  showEve2Banner$: Observable<boolean>;
  showEve23aBanner$: Observable<boolean>;
  eve2BannerUrl = '';

  constructor(
    private store: Store,
    private router: Router,
    private trackingService: TrackingService,
    private amplitudeService: AmplitudeService
  ) {
    this.store.pipe(select(fromAppRoot.selectCustomer), takeUntil(this.onDestroy$)).subscribe((customer: Customer) => {
      this.customer = customer;
      this.eve2BannerUrl = eve2BannerLinks[customer.app_location][customer.language];
      this.oddCheck(this.customer.saxo_data_renewal_expires_at);
    });
    // check the page urls and redirect if it's only just /reports
    const urlParts = this.router.url.split('/');
    if (urlParts[urlParts.length - 1] === 'reports') {
      void this.router.navigate(['reports', 'dashboard']);
    }

    this.store.pipe(select(fromAppRoot.selectCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value;
    });

    this.store
      .select(fromAppRoot.selectGreennessSurveyNotification)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((showGreennessNotification) => {
        this.showGreennessNotification = showGreennessNotification;
      });

    this.showEve2Banner$ = this.store.select(fromAppRoot.selectCustomerFeatureFlag(FeatureFlags.EVE2_BANNER));
    this.showEve23aBanner$ = this.store.select(fromAppRoot.selectCustomerFeatureFlag(FeatureFlags.EVE2_3A_BANNER));
  }

  ngOnInit(): void {
    void this.showGreennessSurvey(this.customer);
    this.store
      .pipe(
        select(fromReporting.selectIsInitialised),
        filter((value) => value === false),
        take(1)
      )
      .subscribe(() => {
        this.initReportingData();
      });

    this.store.dispatch(
      AppRootActions.fetchThirdParties({
        service: `disable_reporting_${this.customer.app_location !== 'ch' ? 'de' : this.customer.app_location}_${
          this.currentAccount.kind === 'kid' ? this.currentAccount.kind : '3b'
        }`
      })
    );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initReportingData(): void {
    this.store.dispatch(AppRootActions.getPromotions());
    this.store.dispatch(ReportingActions.fetchEndValues());
    this.store.dispatch(ReportingActions.fetchTransactions());
    this.store.dispatch(ReportingActions.getGraphData());
    this.store.dispatch(ReportingActions.getCustomerNotifications());
  }

  oddCheck(saxoCheckExpiresAt: null | string) {
    if (saxoCheckExpiresAt === null || typeof saxoCheckExpiresAt === 'undefined') {
      return;
    }
    const hideOddModal: null | boolean = JSON.parse(localStorage.getItem('hideOddModal'));
    this.displayOddModal = hideOddModal === null || !hideOddModal;
  }

  onReview() {
    this.trackingService.trackActivity('[Button] Odd review');
    this.trackOddStartedEvent();
    void this.router.navigate(['/odd']);
  }

  onDoItLater() {
    this.trackingService.trackActivity('[Button] Odd do it later');
    localStorage.setItem('hideOddModal', JSON.stringify(true));
    this.displayOddModal = false;
  }

  async showGreennessSurvey(customer: Customer) {
    const showSurvey = customer.show_greenness_survey;
    const tillDate: null | string = await localStorage.getItem(`hideGreennessSurvey_${customer.id}`);

    if (typeof showSurvey === 'undefined' || showSurvey === null) {
      if (tillDate !== null) {
        localStorage.removeItem(`hideGreennessSurvey_${customer.id}`);
      }
      return;
    }

    if (typeof tillDate !== 'undefined' && tillDate !== null && new Date() < new Date(tillDate)) {
      return;
    }

    this.displayGreennessSurvey = true;
  }

  onDismissSurvey(surveyDialog: boolean) {
    if (surveyDialog) {
      this.displayGreennessSurvey = false;
      const currentDate: Date = new Date();
      const tillDate: string = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem(`hideGreennessSurvey_${this.customer.id}`, tillDate);
    } else {
      this.store.dispatch(AppRootActions.hideSubmitGreennessSurveyNotification());
    }
  }

  onSubmitSurvey(data: { greenness_score?: number; annual_income?: string; net_worth?: string }) {
    this.displayGreennessSurvey = false;
    this.store.dispatch(AppRootActions.submitGreennessSurvey({ data }));
  }

  trackOddStartedEvent() {
    this.amplitudeService.trackEvent('ODD Started', {
      account_bank: 'saxo',
      deadline: this.customer.saxo_data_renewal_expires_at,
      days_to_deadline: getRemainingTime(this.customer.saxo_data_renewal_expires_at).days
    });
  }
}
